import { useEffect, useRef, useState, useCallback } from "react";
import type { Inquiry, Communication } from "../types";
import { STATUS_LABELS, EVENT_TYPE_LABELS, eventSchedule, formatTime12h } from "../types";
import { getCommunications, getReservationCatalog, sendCommunication, syncReservation, updateInquiry, type ReservationCatalog } from "../eventsApi";
import { CommThread } from "./CommThread";
import { EmailComposeModal } from "./EmailComposeModal";
import { ReservationAvailability } from './ReservationAvailability';
import { ReservationQuoteModal } from "./ReservationQuoteModal";
import { ProposalDecision } from "./ProposalDecision";

// Read once per page load. null while "Generate Quote in New System" is switched off.
// The Reservation System's staff site; this domain does not change at the Witchduck rename.
const STAFF_SITE = "https://staff.witchducksocial.com";
let reservationCatalog: Promise<ReservationCatalog | null> | undefined;
function loadReservationCatalog() {
  reservationCatalog ??= getReservationCatalog().catch(err => { reservationCatalog = undefined; throw err; });
  return reservationCatalog;
}

type Props = {
  inquiry: Inquiry;
  onClose: () => void;
  onUpdate: (inquiry: Inquiry) => void;
};

export function LeadDetail({ inquiry, onClose, onUpdate }: Props) {
  const schedule = eventSchedule(inquiry);
  const [comms, setComms] = useState<Communication[]>([]);
  const [channel, setChannel] = useState<Communication["channel"]>("email");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [catalog, setCatalog] = useState<ReservationCatalog | null>(null);
  const [showReservationQuote, setShowReservationQuote] = useState(false);

  useEffect(() => {
    let active = true;
    loadReservationCatalog().then(c => { if (active) setCatalog(c); })
      .catch(err => console.error("Reservation System catalog unavailable", err));
    return () => { active = false; };
  }, []);

  const loadComms = useCallback(async () => {
    try {
      const res = await getCommunications(inquiry.id);
      setComms(res.data);
    } catch (err) {
      console.error("Failed to load communications", err);
    }
  }, [inquiry.id]);

  useEffect(() => { loadComms(); }, [loadComms]);

  // Opening a lead quoted in the Reservation System reads its status once, so a
  // paid or cancelled Reservation shows without waiting for the hourly poll.
  const latest = useRef({ inquiry, onUpdate });
  latest.current = { inquiry, onUpdate };
  const linked = !!inquiry.reservation_id && ["quoted", "deposit_paid", "confirmed", "lost"].includes(inquiry.status);
  useEffect(() => {
    if (!linked) return;
    let active = true;
    syncReservation(inquiry.id).then(result => {
      if (!active) return;
      const { inquiry: current, onUpdate: update } = latest.current;
      update({ ...current, reservation_status: result.status as unknown as Record<string, unknown>,
        status: result.inquiryStatus as Inquiry["status"], reservation_quote: result.reservationQuote, reservation_synced_at: new Date().toISOString() });
    }).catch(err => console.error("Reservation status unavailable", err));
    return () => { active = false; };
  }, [inquiry.id, linked]);

  // "Called?" beside Callback requested (ReservationSystem #469): Yes logs a
  // Phone entry with the notes and mutes the label; No un-mutes and keeps the entry.
  const [callNotes, setCallNotes] = useState<string | null>(null);
  const [savingCall, setSavingCall] = useState(false);
  const [callError, setCallError] = useState("");
  const saveCall = async (save: () => Promise<unknown>, changes: Partial<Inquiry>) => {
    setSavingCall(true); setCallError("");
    try { await save(); onUpdate({ ...inquiry, ...changes }); setCallNotes(null); loadComms(); }
    catch (err) { console.error("Saving the callback failed", err); setCallError("Could not save. Try again."); }
    finally { setSavingCall(false); }
  };
  const handleCalled = () => callNotes?.trim() && saveCall(
    () => sendCommunication(inquiry.id, { channel: "phone", message: `[Phone Call] ${callNotes.trim()}`, callbackCalled: true }),
    { callback_called: true, ...(inquiry.status === "new" ? { status: "contacted" as const } : {}) });
  const handleNotCalled = () => saveCall(() => updateInquiry(inquiry.id, { callback_called: false }), { callback_called: false });

  const handleSendEmail = async (subject: string, body: string) => {
    await sendCommunication(inquiry.id, {
      channel: "email",
      subject: subject || undefined,
      message: body,
    });
    setShowEmailComposer(false);
    loadComms();
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await sendCommunication(inquiry.id, {
        channel,
        message: channel === "phone"
          ? `[Phone Call] ${message.trim()}`
          : channel === "note"
            ? `[Staff Note] ${message.trim()}`
            : message.trim(),
      });
      setMessage("");
      loadComms();
    } catch (err) {
      console.error("Send failed", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-96 flex-shrink-0 border-l border-tribal-border bg-tribal-surface flex flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-tribal-border px-4 py-3">
        <h3 className="text-sm font-semibold text-white">
          {inquiry.first_name} {inquiry.last_name}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-tribal-muted hover:text-white"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="border-b border-tribal-border p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-tribal-muted">Status</span>
            <span className="text-white font-medium">{STATUS_LABELS[inquiry.status]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-tribal-muted">Type</span>
            <span className="text-white">{EVENT_TYPE_LABELS[inquiry.event_type]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-tribal-muted">Group Size</span>
            <span className="text-white">{schedule.groupSize} guests</span>
          </div>
          {schedule.date && (
            <div className="flex justify-between">
              <span className="text-tribal-muted">Date</span>
              <span className="text-white">{schedule.date}</span>
            </div>
          )}
          {schedule.time && (
            <div className="flex justify-between">
              <span className="text-tribal-muted">Start Time</span>
              <span className="text-white">{formatTime12h(schedule.time)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-tribal-muted">Duration</span>
            <span className="text-white">{schedule.hours} hours</span>
          </div>
          <div className="flex justify-between">
            <span className="text-tribal-muted">Email</span>
            <span className="text-white text-xs">{inquiry.email}</span>
          </div>
          {inquiry.phone && (
            <div className="flex justify-between">
              <span className="text-tribal-muted">Phone</span>
              <span className="text-white text-xs">{inquiry.phone}</span>
            </div>
          )}
          {inquiry.food_interest && (
            <div className="flex justify-between">
              <span className="text-tribal-muted">Food</span>
              <span className="text-white">Yes{inquiry.dietary_notes ? ` (${inquiry.dietary_notes})` : ""}</span>
            </div>
          )}
          {inquiry.special_requests && (
            <div className="pt-1">
              <span className="text-tribal-muted text-xs block">Special Requests</span>
              <span className="text-white text-xs">{inquiry.special_requests}</span>
            </div>
          )}
          {inquiry.wants_callback && (
            <div className="pt-1">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${inquiry.callback_called ? "bg-gray-500 text-white" : "bg-amber-500/15 text-amber-400"}`}>
                  📞 Callback requested
                </span>
                <span className="text-xs text-tribal-muted">Called?</span>
                {([true, false] as const).map(yes => (
                  <button key={String(yes)} type="button" aria-pressed={!!inquiry.callback_called === yes} disabled={savingCall}
                    onClick={() => yes ? !inquiry.callback_called && setCallNotes("") : inquiry.callback_called && handleNotCalled()}
                    className={`rounded px-2 py-0.5 text-xs ${!!inquiry.callback_called === yes ? "bg-tribal-red text-white" : "bg-tribal-border text-tribal-muted hover:text-white"}`}>
                    {yes ? "Yes" : "No"}
                  </button>
                ))}
              </div>
              {callNotes !== null && (
                <form className="mt-2 space-y-1" onSubmit={e => { e.preventDefault(); handleCalled(); }}>
                  <label className="block text-xs text-tribal-muted">
                    Call notes
                    <textarea required value={callNotes} onChange={e => setCallNotes(e.target.value)} rows={3}
                      className="mt-1 w-full rounded border border-tribal-border bg-tribal-bg p-2 text-xs text-white" />
                  </label>
                  <div className="flex gap-2">
                    <button type="submit" disabled={savingCall || !callNotes.trim()} className="rounded bg-tribal-red px-2 py-1 text-xs text-white disabled:opacity-50">Save call</button>
                    <button type="button" onClick={() => setCallNotes(null)} className="rounded bg-tribal-border px-2 py-1 text-xs text-tribal-muted">Cancel</button>
                  </div>
                </form>
              )}
              {callError && <p className="mt-1 text-xs text-tribal-red">{callError}</p>}
              {inquiry.callback_number && (
                <span className="block text-xs text-white mt-1">
                  {inquiry.callback_number}
                </span>
              )}
            </div>
          )}
          {inquiry.checkfront_booking_url && (
            <div className="pt-1">
              <a
                href={inquiry.checkfront_booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-tribal-red hover:underline"
              >
                View Checkfront Booking →
              </a>
            </div>
          )}

          <ReservationAvailability key={`${inquiry.id}/${inquiry.group_size}/${inquiry.duration_hours}`} inquiryId={inquiry.id} dates={inquiry.preferred_dates.map(d => d.date)} />
          {/* Quote action: an inquiry takes one payment path, Checkfront or the Reservation System. */}
          <div className="pt-2 space-y-2">
            {inquiry.reservation_code && (
              <span className="inline-block rounded-full bg-green-600/20 px-2 py-0.5 text-xs text-green-400">
                ✓ Reservation {inquiry.reservation_code}{inquiry.reservation_quote ? ` · Quote v${inquiry.reservation_quote.version}` : ""}
              </span>
            )}
            {/* Once quoted, the Reservation is edited and its Proposal sent on the staff site (ReservationSystem #467). */}
            {inquiry.reservation_id ? (
              <a
                href={`${STAFF_SITE}/reservations/${encodeURIComponent(inquiry.reservation_id)}#group-quotes`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full rounded-md bg-tribal-red px-3 py-2 text-center text-xs font-medium text-white hover:bg-tribal-red-dark transition-colors"
              >
                Open in New System →
              </a>
            ) : catalog && !inquiry.checkfront_booking_code && (
              <button
                type="button"
                onClick={() => setShowReservationQuote(true)}
                className="w-full rounded-md bg-tribal-red px-3 py-2 text-xs font-medium text-white hover:bg-tribal-red-dark transition-colors"
              >
                Generate Quote in New System →
              </button>
            )}
            <ProposalDecision inquiry={inquiry} onDone={result => {
              onUpdate({ ...inquiry, reservation_status: result.status as unknown as Record<string, unknown>, status: result.inquiryStatus as Inquiry["status"],
                reservation_quote: result.reservationQuote, reservation_synced_at: new Date().toISOString() });
              loadComms();
            }} />
            {/* Quoted on Checkfront before the cutover; new quotes are made in the Reservation System only (#30). */}
            {!inquiry.reservation_id && inquiry.checkfront_booking_code && (
              <span className="inline-block rounded-full bg-green-600/20 px-2 py-0.5 text-xs text-green-400">
                ✓ Quote sent
              </span>
            )}
          </div>
        </div>

        <div className="border-b border-tribal-border p-4">
          <h4 className="text-xs font-semibold uppercase text-tribal-muted mb-2">
            Communication ({comms.length})
          </h4>
          <CommThread communications={comms} />
        </div>
      </div>

      {showReservationQuote && catalog && (
        <ReservationQuoteModal
          inquiry={inquiry}
          catalog={catalog}
          onClose={() => { setShowReservationQuote(false); loadComms(); }}
          onChanged={(patch) => onUpdate({ ...inquiry, ...patch })}
        />
      )}

      {showEmailComposer && (
        <EmailComposeModal
          inquiry={inquiry}
          onSend={handleSendEmail}
          onClose={() => setShowEmailComposer(false)}
        />
      )}

      <div className="border-t border-tribal-border p-3 space-y-2">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setChannel("email")}
            className={`rounded px-2 py-1 text-xs font-medium ${
              channel === "email"
                ? "bg-tribal-red text-white"
                : "bg-tribal-border text-tribal-muted"
            }`}
          >
            Email
          </button>
          <button
            type="button"
            onClick={() => setChannel("sms")}
            disabled={!inquiry.phone}
            className={`rounded px-2 py-1 text-xs font-medium ${
              channel === "sms"
                ? "bg-tribal-red text-white"
                : "bg-tribal-border text-tribal-muted"
            } disabled:opacity-40`}
          >
            SMS
          </button>
          <button
            type="button"
            onClick={() => setChannel("phone")}
            disabled={!inquiry.phone && !inquiry.callback_number}
            className={`rounded px-2 py-1 text-xs font-medium ${
              channel === "phone"
                ? "bg-amber-500 text-white"
                : "bg-tribal-border text-tribal-muted"
            } disabled:opacity-40`}
          >
            Phone
          </button>
          <button
            type="button"
            onClick={() => setChannel("note")}
            className={`rounded px-2 py-1 text-xs font-medium ${
              channel === "note"
                ? "bg-blue-500 text-white"
                : "bg-tribal-border text-tribal-muted"
            }`}
          >
            Note
          </button>
        </div>

        {channel === "email" ? (
          <button
            type="button"
            onClick={() => setShowEmailComposer(true)}
            className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-3 text-xs text-tribal-muted hover:border-tribal-red hover:text-white transition-colors text-left"
          >
            Click to compose email…
          </button>
        ) : (
          <>
            {channel === "phone" && (
              <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 text-xs text-amber-400">
                📞 {inquiry.callback_number || inquiry.phone}
              </div>
            )}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder={
                channel === "phone"
                  ? "Note date and time of call and important details."
                  : channel === "note"
                    ? "Add an internal note about this reservation…"
                    : "Type your SMS message…"
              }
              className={`w-full resize-none rounded border bg-tribal-bg px-2 py-1.5 text-xs text-white placeholder-tribal-muted focus:outline-none ${
                channel === "note"
                  ? "border-blue-500/30 focus:border-blue-500"
                  : "border-tribal-border focus:border-tribal-red"
              }`}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className={`w-full rounded py-1.5 text-xs font-medium text-white disabled:opacity-50 ${
                channel === "phone"
                  ? "bg-amber-500 hover:bg-amber-600"
                  : channel === "note"
                    ? "bg-blue-500 hover:bg-blue-600"
                    : "bg-tribal-red hover:bg-tribal-red-dark"
              }`}
            >
              {sending
                ? channel === "sms" ? "Sending…" : "Saving…"
                : channel === "phone" ? "Log Phone Call" : channel === "note" ? "Save Note" : "Send"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
